import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { programsAPI, exercisesAPI, exercisePlansAPI } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Plus, Save, Dumbbell, Trash2, Layers } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = { performance: "Rendimiento", clinical: "Clínico" };
const EMPTY_ITEM = { exercise_id: "", sets: 3, reps: "10", load_percent_rm: "", rpe: "", notes: "" };

export default function ProgramDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cycle, setCycle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState([]);
  const [showPlan, setShowPlan] = useState(false);
  const [planTitle, setPlanTitle] = useState("");
  const [planDesc, setPlanDesc] = useState("");
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [saving, setSaving] = useState(false);

  const fetchCycle = useCallback(async () => {
    try {
      const res = await programsAPI.get(id);
      setCycle(res.data);
    } catch {
      toast.error("Error cargando el programa");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchCycle(); }, [fetchCycle]);
  useEffect(() => {
    exercisesAPI.list().then((r) => setCatalog(r.data.exercises || [])).catch(() => {});
  }, []);

  const updateItem = (i, k, v) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((arr) => [...arr, { ...EMPTY_ITEM }]);
  const removeItem = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));

  const savePlan = async (e) => {
    e.preventDefault();
    const valid = items.filter((it) => it.exercise_id);
    if (!planTitle.trim()) { toast.error("El título del plan es obligatorio"); return; }
    if (!valid.length) { toast.error("Agrega al menos un ejercicio del catálogo"); return; }
    setSaving(true);
    try {
      await exercisePlansAPI.create({
        program_cycle_id: id,
        patient_id: cycle.patient_id,
        title: planTitle,
        description: planDesc,
        items: valid.map((it) => ({
          exercise_id: it.exercise_id,
          sets: Number(it.sets) || 0,
          reps: String(it.reps || ""),
          notes: it.notes || "",
          ...(it.load_percent_rm !== "" ? { load_percent_rm: Number(it.load_percent_rm) } : {}),
          ...(it.rpe !== "" ? { rpe: Number(it.rpe) } : {}),
        })),
      });
      toast.success("Plan agregado");
      setShowPlan(false);
      setPlanTitle("");
      setPlanDesc("");
      setItems([{ ...EMPTY_ITEM }]);
      fetchCycle();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="space-y-4"><div className="h-28 bg-muted rounded-xl animate-pulse" /><div className="h-48 bg-muted rounded-xl animate-pulse" /></div>;
  }
  if (!cycle) {
    return <div className="text-center py-16"><p className="text-muted-foreground">Programa no encontrado</p><Button variant="link" onClick={() => navigate("/programas")}>Volver</Button></div>;
  }

  const plans = cycle.exercise_plans || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="program-detail-page">
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" className="mt-1" onClick={() => navigate("/programas")} data-testid="back-to-programs">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl sm:text-2xl font-bold text-foreground">{cycle.title}</h1>
            <Badge variant="secondary" className="text-[10px]">{TYPE_LABELS[cycle.type] || cycle.type}</Badge>
            <Badge variant={cycle.status === "active" ? "default" : "secondary"} className="text-[10px]">
              {cycle.status === "active" ? "Activo" : "Completado"}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {cycle.patient?.name || "—"}{cycle.weeks ? ` · ${cycle.weeks} semanas` : ""}{cycle.goal ? ` · ${cycle.goal}` : ""}
          </p>
        </div>
        <Button size="sm" className="rounded-full" onClick={() => { setPlanTitle(""); setPlanDesc(""); setItems([{ ...EMPTY_ITEM }]); setShowPlan(true); }} data-testid="new-program-plan-btn">
          <Plus className="w-4 h-4 mr-1.5" />Nuevo plan
        </Button>
      </div>

      {plans.length > 0 ? (
        plans.map((plan) => (
          <Card key={plan.id} data-testid={`program-plan-${plan.id}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{plan.title}</CardTitle>
              {plan.description && <p className="text-xs text-muted-foreground mt-1">{plan.description}</p>}
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(plan.exercises || []).map((ex, i) => (
                  <div key={i} className="p-3 rounded-lg bg-muted/50 flex items-start gap-3">
                    <Dumbbell className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{ex.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {ex.sets} series x {ex.reps} reps{ex.frequency ? ` · ${ex.frequency}` : ""}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          <Layers className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Sin planes en este programa</p>
        </div>
      )}

      <Dialog open={showPlan} onOpenChange={setShowPlan}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="program-plan-dialog">
          <DialogHeader><DialogTitle>Nuevo plan</DialogTitle></DialogHeader>
          <form onSubmit={savePlan} className="space-y-4">
            <div>
              <Label>Título del plan *</Label>
              <Input value={planTitle} onChange={(e) => setPlanTitle(e.target.value)} placeholder="Ej: Semana 1 - Fuerza" required data-testid="program-plan-title" />
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea value={planDesc} onChange={(e) => setPlanDesc(e.target.value)} rows={2} placeholder="Indicaciones generales..." data-testid="program-plan-desc" />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="mb-0">Ejercicios</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="program-add-item">
                  <Plus className="w-3 h-3 mr-1" />Agregar
                </Button>
              </div>
              {items.map((it, i) => (
                <Card key={i} className="bg-muted/30">
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">Ejercicio {i + 1}</span>
                      {items.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem(i)} data-testid={`program-remove-item-${i}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                    <Select value={it.exercise_id} onValueChange={(v) => updateItem(i, "exercise_id", v)}>
                      <SelectTrigger data-testid={`program-item-select-${i}`}>
                        <SelectValue placeholder="Selecciona un ejercicio del catálogo" />
                      </SelectTrigger>
                      <SelectContent>
                        {catalog.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <Label className="text-xs">Series</Label>
                        <Input type="number" value={it.sets} onChange={(e) => updateItem(i, "sets", Number(e.target.value))} data-testid={`program-item-sets-${i}`} />
                      </div>
                      <div>
                        <Label className="text-xs">Reps</Label>
                        <Input value={it.reps} onChange={(e) => updateItem(i, "reps", e.target.value)} placeholder="8-12" data-testid={`program-item-reps-${i}`} />
                      </div>
                      <div>
                        <Label className="text-xs">%RM</Label>
                        <Input type="number" value={it.load_percent_rm} onChange={(e) => updateItem(i, "load_percent_rm", e.target.value)} placeholder="80" data-testid={`program-item-rm-${i}`} />
                      </div>
                      <div>
                        <Label className="text-xs">RPE</Label>
                        <Input type="number" value={it.rpe} onChange={(e) => updateItem(i, "rpe", e.target.value)} placeholder="8" data-testid={`program-item-rpe-${i}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowPlan(false)}>Cancelar</Button>
              <Button type="submit" className="rounded-full" disabled={saving} data-testid="program-plan-save-btn">
                <Save className="w-4 h-4 mr-1.5" />{saving ? "Guardando..." : "Agregar plan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
